/**
 * CDM-PDD Layout Component
 * 專門用於 CDM-PDD Version 12.0 的兩層導航佈局
 */
import { useState, useEffect } from 'react';
import { Layout, Menu, Breadcrumb, Card, Tag, Progress, Grid } from 'antd';
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { pddNavigation, type PDDNavItem, getNavItemByKey } from '@/config/pddNavigation';
import type { MenuProps } from 'antd';

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const PDDLayout: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { projectId } = params;

  const [collapsed, setCollapsed] = useState(isMobile);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // 根據當前路徑設定選中的菜單
  useEffect(() => {
    const path = location.pathname;

    // 從所有導航項目中找到匹配的
    const findMatchingKeys = (items: PDDNavItem[], parentKey?: string): { selected?: string; open?: string } => {
      for (const item of items) {
        if (item.path && path.includes(item.key)) {
          return {
            selected: item.key,
            open: parentKey,
          };
        }
        if (item.children) {
          const result = findMatchingKeys(item.children, item.key);
          if (result.selected) {
            return result;
          }
        }
      }
      return {};
    };

    const { selected, open } = findMatchingKeys(pddNavigation);
    if (selected) {
      setSelectedKeys([selected]);
      if (open) {
        setOpenKeys([open]);
      }
    }
  }, [location.pathname]);

  // 轉換導航配置為 Ant Design Menu 格式
  const convertToMenuItems = (items: PDDNavItem[]): MenuProps['items'] => {
    return items.map(item => {
      const Icon = item.icon;
      const label = (
        <span>
          {item.label}
          {item.badge && (
            <Tag
              color={
                item.badge === 'NEW' ? 'green' :
                item.badge === 'MOVED' ? 'blue' :
                'orange'
              }
              style={{ marginLeft: 8, fontSize: 10 }}
            >
              {item.badge}
            </Tag>
          )}
          {item.required && (
            <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
          )}
        </span>
      );

      if (item.children && item.children.length > 0) {
        return {
          key: item.key,
          icon: Icon ? <Icon /> : null,
          label,
          children: item.children.map(child => ({
            key: child.key,
            label: (
              <span>
                {child.label}
                {child.badge && (
                  <Tag
                    color={
                      child.badge === 'NEW' ? 'green' :
                      child.badge === 'MOVED' ? 'blue' :
                      'orange'
                    }
                    style={{ marginLeft: 8, fontSize: 10 }}
                  >
                    {child.badge}
                  </Tag>
                )}
                {child.required && (
                  <span style={{ color: '#ff4d4f', marginLeft: 4 }}>*</span>
                )}
              </span>
            ),
            type: child.type,
          })),
        };
      }

      return {
        key: item.key,
        icon: Icon ? <Icon /> : null,
        label,
      };
    });
  };

  // 處理菜單點擊
  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    const navItem = getNavItemByKey(key);
    if (navItem?.path && projectId) {
      const path = navItem.path.replace(':projectId', projectId);
      navigate(path);
    }
  };

  // 處理子菜單展開/收合
  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  // 產生麵包屑
  const generateBreadcrumbs = () => {
    const path = location.pathname;
    const items: { title: string }[] = [
      { title: '專案管理' },
      { title: `專案 ${projectId}` },
    ];

    // 找到當前頁面對應的導航項目
    const findCurrentNav = (navItems: PDDNavItem[]): PDDNavItem | null => {
      for (const item of navItems) {
        if (item.path && path.includes(item.key)) {
          return item;
        }
        if (item.children) {
          const found = findCurrentNav(item.children);
          if (found) return found;
        }
      }
      return null;
    };

    const currentNav = findCurrentNav(pddNavigation);
    if (currentNav) {
      items.push({ title: currentNav.label });
    }

    return items;
  };

  // 計算完成度
  const calculateProgress = () => {
    // TODO: 從 API 獲取實際完成度
    return 35; // 暫時固定值
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 左側導航 */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={280}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
        breakpoint="lg"
      >
        {/* PDD 標題 */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: collapsed ? 14 : 16,
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          {collapsed ? 'PDD' : 'CDM-PDD v12.0'}
        </div>

        {/* 進度指示 */}
        {!collapsed && (
          <div style={{ padding: 16, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: 8, fontSize: 12 }}>
              文件完成度
            </div>
            <Progress
              percent={calculateProgress()}
              size="small"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
          </div>
        )}

        {/* 導航菜單 */}
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          onClick={handleMenuClick}
          items={convertToMenuItems(pddNavigation)}
          style={{ borderRight: 0 }}
        />
      </Sider>

      {/* 右側內容 */}
      <Layout style={{ marginLeft: collapsed ? 80 : 280, transition: 'margin-left 0.2s' }}>
        <Content style={{ padding: isMobile ? 12 : 24, background: '#f0f2f5' }}>
          {/* 麵包屑 */}
          <Breadcrumb items={generateBreadcrumbs()} style={{ marginBottom: 16 }} />

          {/* 頁面提示卡片 */}
          <Card
            size="small"
            style={{ marginBottom: 16 }}
            bodyStyle={{ padding: '12px 16px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontWeight: 500 }}>📋 CDM-PDD Version 12.0</span>
                <span style={{ marginLeft: 12, color: '#666', fontSize: 13 }}>
                  請依照 UNFCCC 官方格式填寫
                </span>
              </div>
              <div>
                <Tag color="red">必填</Tag>
                <Tag color="green">NEW</Tag>
                <Tag color="blue">MOVED</Tag>
                <Tag color="orange">UPGRADED</Tag>
              </div>
            </div>
          </Card>

          {/* 子路由內容 */}
          <div style={{ background: '#fff', padding: isMobile ? 16 : 24, borderRadius: 8 }}>
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default PDDLayout;
